import React, { createContext, useState, useContext, useEffect } from "react";

const STORAGE_KEY = 'casa_card_states';

const CardStateContext = createContext();

const loadPersistedState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        cardStates: parsed.cardStates || {},
        selectedState: parsed.selectedState || null,
      };
    }
  } catch (_) {}
  return { cardStates: {}, selectedState: null };
};

export const CardStateProvider = ({ children }) => {
  const persisted = loadPersistedState();

  const [cardStates, setCardStates] = useState(persisted.cardStates);
  const [selectedState, setSelectedState] = useState(persisted.selectedState);
  // The card most recently opened/toggled, so it can be raised above any
  // other cards left expanded alongside it (see handleCardClick below) —
  // without this, whichever card sits earlier in the grid always wins the
  // stacking fight (landing.css's ".expanded-col:nth-child(n)"), so a
  // dropdown etc. in a later card could never be clicked while an earlier
  // one was also expanded.
  const [topIndex, setTopIndex] = useState(null);

  // Restore showNewsFlow from persisted cardStates (card index 0 = News)
  const [showNewsFlow, setShowNewsFlow] = useState(() => {
    const v = persisted.cardStates['0'];
    return v === 'expanded' || v === 'fullscreen';
  });

  // Persist to localStorage whenever cardStates or selectedState changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ cardStates, selectedState }));
    } catch (_) {}
  }, [cardStates, selectedState]);

  const handleCardClick = (index) => {
    // Whichever card was just opened/toggled is the one the guest is about
    // to interact with — see topIndex's z-index use in Landing.js.
    setTopIndex(index);
    setCardStates((prev) => {
      const currentState = prev[index] || "inactive";
      let nextState;
      if (currentState === "inactive") {
        nextState = "expanded";
      } else if (currentState === "expanded") {
        nextState = "fullscreen";
      } else {
        nextState = "inactive";
      }

      // Other cards keep whatever state they were already in — opening one
      // card no longer closes the others.
      const newStates = { ...prev, [index]: nextState };

      if (index === 0) {
        setShowNewsFlow(nextState === "expanded" || nextState === "fullscreen");
      }
      return newStates;
    });
  };

  const handleClose = (index) => {
    setCardStates((prev) => {
      const currentState = prev[index];
      let newState;
      if (currentState === "fullscreen") {
        newState = "expanded";
      } else {
        newState = "inactive";
      }

      const newStates = { ...prev, [index]: newState };
      if (index === 0) {
        setShowNewsFlow(newState === "expanded" || newState === "fullscreen");
        if (newState === "inactive") {
          setSelectedState(null);
        }
      }
      return newStates;
    });
  };

  return (
    <CardStateContext.Provider
      value={{
        cardStates,
        handleCardClick,
        handleClose,
        showNewsFlow,
        setShowNewsFlow,
        selectedState,
        setSelectedState,
        setCardStates,
        topIndex,
      }}
    >
      {children}
    </CardStateContext.Provider>
  );
};

export const useCardState = () => useContext(CardStateContext);
